var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from "react";
let productsCache = {};
export const useCartManager = () => {
    const [items, setItems] = useState([]);
    const [cartOpen, setCartOpen] = useState(false);
    const [performingAction, setPerformingAction] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState(globalThis.cartDrawerCategories);
    const [products, setProducts] = useState(globalThis.cartDrawerCollection);
    const [alternateVersion, setAlternateVersion] = useState(globalThis.cartDrawerSeeBVersion);
    const [emptyStateTitle, setEmptyStateTitle] = useState(globalThis.cartDrawerEmptyStateTitle);
    const [emptyStateSubtitle, setEmptyStateSubtitle] = useState(globalThis.cartDrawerEmptyStateSubtitle);
    const [emptyStateCTAText, setEmptyStateCTAText] = useState(globalThis.cartDrawerEmptyStateCTAText);
    const [emptyStateCTALink, setEmptyStateCTALink] = useState(globalThis.cartDrawerEmptyStateCTALink);
    const [emptyStateCTASubtext, setEmptyStateCTASubtext] = useState(globalThis.cartDrawerEmptyStateCTASubtext);
    const [subscriptionInfoDetails, setSubscriptionInfoDetails] = useState(globalThis.cartDrawerSubscriptionInfoDetails);
    const [termsURL, setTermsURL] = useState(globalThis.cartDrawerTermsURL);
    const [totalDiscount, setTotalDiscount] = useState(0);
    /**
     * Data for the drawer's empty state.
     *
     * Each one corresponds to all the experimental variations being tested.
     */
    let emptyStateCategories;
    let emptyStateProducts;
    let seeBVersion;
    const getCart = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setPerformingAction(true);
            const response = yield fetch('/cart.js', {
                method: 'GET',
                mode: 'same-origin',
                cache: 'no-cache',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const cart = yield response.json();
            for (let cartItem of cart.items) {
                let productInfo = productsCache[cartItem.id];
                if (!productInfo) {
                    productInfo = yield fetch(`/products/${cartItem.handle}.js`, {
                        method: 'GET',
                        mode: 'same-origin',
                        cache: 'no-cache',
                        credentials: 'same-origin',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });
                    productInfo = yield productInfo.json();
                    productsCache[cartItem.id] = productInfo;
                }
                cartItem.product = productInfo;
                cartItem.isSubscribable = !!productInfo.selling_plan_groups[0];
            }
            setItems(cart.items);
            setTotalDiscount(cart.total_discount / 100);
            updateCartCount(cart.items);
            window.cartManager.items = cart.items;
            setPerformingAction(false);
        }
        catch (error) {
            console.log("CartDrawer unable to load.");
        }
        finally {
            setPerformingAction(false);
            setIsLoading(false);
        }
    });
    const updateCartCount = (items) => {
        const cartElements = document.querySelectorAll('.cartCount');
        const qty = items.length > 0 ? items.reduce((previous, item) => previous + item.quantity, 0) : 0;
        if (cartElements.length > 0) {
            for (const cartElement of cartElements) {
                cartElement.innerHTML = qty.toString();
            }
        }
    };
    const addItem = (items) => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        const response = yield fetch('/cart/add.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(items),
        });
        yield getCart();
        openCart();
        setPerformingAction(false);
    });
    const removeItem = (itemKey) => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        const response = yield fetch('/cart/update.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                updates: {
                    [itemKey]: 0,
                }
            })
        });
        yield getCart();
        setPerformingAction(false);
    });
    const updateItem = (data) => __awaiter(void 0, void 0, void 0, function* () {
        yield fetch('/cart/update.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        yield getCart();
    });
    const changeItem = (item, optionIndex, cartItemIndex) => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        //get product and its selling plan
        const response = yield fetch(`/products/${item.handle}.js`, {
            method: 'GET',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const product = yield response.json();
        const data = {
            line: cartItemIndex,
            quantity: item.quantity,
            selling_plan: product.selling_plan_groups[0].selling_plans[optionIndex].id,
        };
        yield fetch('/cart/change.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        yield getCart();
        setPerformingAction(false);
    });
    const removeSubscription = (item, cartItemIndex) => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        const data = {
            line: cartItemIndex,
            quantity: item.quantity,
            selling_plan: null,
        };
        yield fetch('/cart/change.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });
        yield getCart();
        setPerformingAction(false);
    });
    const increaseQty = (item) => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        const data = {
            updates: {
                [item.key]: item.quantity + 1,
            }
        };
        yield updateItem(data);
        setPerformingAction(false);
    });
    const decreaseQty = (item) => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        const data = {
            updates: {
                [item.key]: item.quantity - 1,
            }
        };
        yield updateItem(data);
        setPerformingAction(false);
    });
    const clearCart = () => __awaiter(void 0, void 0, void 0, function* () {
        setPerformingAction(true);
        yield fetch('/cart/clear.js', {
            method: 'POST',
            mode: 'same-origin',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(""),
        });
        yield getCart();
        setPerformingAction(false);
    });
    const openCart = () => {
        setCartOpen(true);
        window.cartManager.cartOpen = true;
    };
    const closeCart = () => {
        setCartOpen(false);
        window.cartManager.cartOpen = false;
    };
    // exposing cart manager to window
    window.cartManager = {
        categories,
        products,
        alternateVersion,
        setCategories,
        setProducts,
        setAlternateVersion,
        items,
        totalDiscount,
        cartOpen,
        performingAction,
        isLoading,
        addItem,
        removeItem,
        updateItem,
        removeSubscription,
        changeItem,
        clearCart,
        openCart,
        closeCart,
        increaseQty,
        decreaseQty,
        getCart,
        emptyStateTitle,
        setEmptyStateTitle,
        emptyStateSubtitle,
        setEmptyStateSubtitle,
        emptyStateCTAText,
        setEmptyStateCTAText,
        emptyStateCTALink,
        setEmptyStateCTALink,
        emptyStateCTASubtext,
        setEmptyStateCTASubtext,
        subscriptionInfoDetails,
        setSubscriptionInfoDetails,
        termsURL,
        setTermsURL
    };
    return { items, totalDiscount, cartOpen, performingAction, isLoading, getCart, addItem, removeItem, updateItem, removeSubscription, changeItem, clearCart, openCart, closeCart, increaseQty, decreaseQty, categories, setCategories, products, setProducts, alternateVersion, setAlternateVersion, emptyStateTitle, setEmptyStateTitle, emptyStateSubtitle, setEmptyStateSubtitle, emptyStateCTAText, setEmptyStateCTAText, emptyStateCTALink, setEmptyStateCTALink, emptyStateCTASubtext, setEmptyStateCTASubtext, subscriptionInfoDetails, setSubscriptionInfoDetails, termsURL, setTermsURL };
};
